import React from 'react';
import Table from 'components/Table';
import {
  numberToCurrencyString,
  dateToLabel,
  numberToDisplayString,
} from 'further-ui/utils';

import { Api } from 'further-types/investment';
import useStyles from './styles';
import { Box, styled, Tooltip } from '@mui/material';
import { CompareArrows, InfoOutlined } from '@mui/icons-material';
import {
  createCompanyEditRoute,
  createInvestmentViewLink,
} from 'adminConstants/routes';
import { Link } from 'react-router-dom';

const StyledInfoOutlined = styled(InfoOutlined)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));

type Exit = Api.GetInvestmentExitsResponse['exits'][number];

type Props = {
  exitedShareholdings: Array<Exit>;
  loading: boolean;
};

const ShareholdingCompanyName: React.FC<{
  exit: Exit;
}> = ({ exit }) => {
  const { classes } = useStyles();

  const companyName = exit.company.id ? (
    <Link className={classes.link} to={createCompanyEditRoute(exit.company.id)}>
      {exit.company.name}
    </Link>
  ) : (
    exit.company.name
  );

  if (!exit.isTransferredOut && !exit.isTransferredIn)
    return <>{companyName}</>;

  const tooltipContent = exit.isTransferredOut ? (
    <div>
      {exit.noOfShares} exited shares were transferred to{' '}
      <a
        className={classes.tooltipLink}
        href={createInvestmentViewLink(exit.investmentId)}
        target="_blank"
        rel="noreferrer"
      >
        {exit.investorName}
      </a>{' '}
      on {dateToLabel(exit.transferDate)}.
    </div>
  ) : (
    <div>
      {exit.noOfShares} exited shares were transferred from{' '}
      <a
        className={classes.tooltipLink}
        href={createInvestmentViewLink(exit.sourceInvestmentId ?? '')}
        target="_blank"
        rel="noreferrer"
      >
        {exit.sourceInvestorName}
      </a>{' '}
      on {dateToLabel(exit.transferDate)}.
    </div>
  );

  return (
    <>
      <Tooltip title={tooltipContent}>
        <Box alignItems="center" display="flex" gap={4}>
          {companyName} <CompareArrows className={classes.splitIcon} />
        </Box>
      </Tooltip>
    </>
  );
};

const ExitedShareholdings: React.FC<Props> = ({
  exitedShareholdings,
  loading,
}) => {
  const { classes } = useStyles();

  const columns = [
    {
      label: 'Company',
      key: 'companyName',
      render: (elm: Exit) => <ShareholdingCompanyName exit={elm} />,
    },
    {
      label: 'Exit date',
      key: 'exitDate',
      render: (elm: Exit) => dateToLabel(elm.date),
    },
    {
      label: 'Investment date',
      key: 'allocationDate',
      render: (elm: Exit) => {
        return elm.isAmendment ? '(Update)' : dateToLabel(elm.allocationDate);
      },
    },
    {
      label: 'Capital allocated',
      key: 'capitalAllocated',
      render: (elm: Exit) =>
        elm.isAmendment
          ? ''
          : numberToCurrencyString(elm.capitalAllocated, { fallback: '-' }),
    },
    {
      label: 'Shares exited',
      key: 'noOfShares',
      render: (elm: Exit) =>
        elm.isAmendment ? '' : numberToDisplayString(elm?.noOfShares),
    },
    {
      label: 'Initial share price',
      key: 'intitalSharePrice',
      render: (elm: Exit) => {
        return elm.isAmendment
          ? ''
          : numberToCurrencyString(elm.initialSharePrice, {
              unlimitedDp: true,
            });
      },
    },
    {
      label: 'Exit share price',
      key: 'exitedSharePrice',
      render: (elm: Exit) => {
        const value = elm?.sharePrice;
        return numberToCurrencyString(value, { unlimitedDp: true });
      },
    },
    {
      label: 'Gross realised value',
      key: 'valueBeforeDeductions',
      render: (elm: Exit) => numberToCurrencyString(elm.valueBeforeDeductions),
    },
    {
      label: 'Share consideration value',
      key: 'shareConsiderationValue',
      render: (elm: Exit) =>
        numberToCurrencyString(elm.shareConsiderationValue ?? 0),
    },
    {
      label: 'Performance fee',
      key: 'performanceFee',
      render: (elm: Exit) => numberToCurrencyString(elm.performanceFeesCharged),
    },
    {
      label: 'Accrued fees',
      key: 'accruedFeesCharged',
      render: (elm: Exit) => numberToCurrencyString(elm.accruedFeesCharged),
    },
    {
      label: 'Net exit value',
      key: 'netExitValue',
      render: (elm: Exit) => numberToCurrencyString(elm.netExitValue),
    },
    {
      label: 'Gain/Loss',
      key: 'gainOrLoss',
      render: (elm: Exit) => (
        <div className={classes.noWrap}>
          {numberToCurrencyString(elm.gainLossValue, {
            fallback: '-',
          })}
        </div>
      ),
    },
    {
      label: 'Income tax relief',
      key: 'incomeTaxRelief',
      render: (elm: Exit) =>
        elm.isAmendment || !elm.taxCertificateDetails?.taxReliefEligible
          ? ''
          : numberToCurrencyString(elm.incomeTaxRelief),
    },
  ];

  const getTotalCapitalAllocatedAndExitValue = (dataArray: Array<Exit>) => {
    let totalCapitalAllocated = 0;
    let totalExitValue = 0;
    let grossRealisedValue = 0;
    let shareConsiderationValue = 0;
    let performanceFee = 0;
    let gainLoss = 0;
    let accruedFees = 0;

    for (const item of dataArray) {
      if (item.isTransferredOut) continue;

      totalCapitalAllocated += item.isAmendment ? 0 : item.capitalAllocated;
      totalExitValue +=
        item.valueAfterDeductions - (item.accruedFeesCharged ?? 0);
      performanceFee += item.performanceFeesCharged;
      // Only use gross realised value for totals (ignore share consideration value)
      grossRealisedValue += item.valueBeforeDeductions;
      shareConsiderationValue += item.shareConsiderationValue ?? 0;
      gainLoss += item.gainLossValue;
      accruedFees += item.accruedFeesCharged;
    }

    return {
      totalCapitalAllocated: numberToCurrencyString(totalCapitalAllocated),
      totalExitValue: numberToCurrencyString(totalExitValue),
      grossRealisedValue: numberToCurrencyString(grossRealisedValue),
      shareConsiderationValue: numberToCurrencyString(shareConsiderationValue),
      performanceFee: numberToCurrencyString(performanceFee),
      accruedFees: numberToCurrencyString(accruedFees),
      gainLoss: numberToCurrencyString(gainLoss),
    };
  };

  const totals = getTotalCapitalAllocatedAndExitValue(exitedShareholdings);
  const hasTransferredOut = exitedShareholdings.some(
    (exit) => exit.isTransferredOut,
  );

  return (
    <Table
      //@ts-expect-error
      columns={columns.map((col) => ({
        ...col,
        sort: false,
        getCellClassName: (exit: Exit & { isTransferredOut: boolean }) =>
          `${exit.isTransferredOut ? classes.inactiveCell : ''} ${
            exit.isAmendment ? classes.amendmentCell : ''
          }`,
      }))}
      loading={loading}
      tablebody={exitedShareholdings}
      pagination={false}
      emptyMessage="No exited shareholdings found"
      variant="nohover"
      TableFooter={
        <tfoot>
          <tr className={classes.summaryRow}>
            <td className={classes.summaryValue} colSpan={2} />
            <td className={classes.summaryValue}>
              <Box display="flex" alignItems="center" whiteSpace="nowrap">
                Subscription total:
                {hasTransferredOut && (
                  <Tooltip title="Amounts in this table also relate to transferred out shareholdings. This total is the amount currently relevant to this subscription.">
                    <StyledInfoOutlined fontSize="small" />
                  </Tooltip>
                )}
              </Box>
            </td>
            <td className={classes.summaryValue}>
              {totals.totalCapitalAllocated}
            </td>
            <td className={classes.summaryValue} colSpan={3} />
            <td className={classes.summaryValue}>
              {totals.grossRealisedValue}
            </td>
            <td className={classes.summaryValue}>
              {totals.shareConsiderationValue}
            </td>
            <td className={classes.summaryValue}>{totals.performanceFee}</td>
            <td className={classes.summaryValue}>{totals.accruedFees}</td>
            <td className={classes.summaryValue}>{totals.totalExitValue}</td>
            <td className={classes.summaryValue} colSpan={3}>
              {totals.gainLoss}
            </td>
          </tr>
        </tfoot>
      }
    />
  );
};

export default ExitedShareholdings;
